import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "./Logo";
import NexaCore from "./NexaCore";
import { cn } from "../lib/utils";

function TopNavLink({ to, testId, children }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          "relative inline-flex items-center px-1 py-1 text-sm font-medium transition-colors",
          isActive ? "text-white" : "text-white/60 hover:text-white",
        )
      }
    >
      {({ isActive }) => (
        <>
          <span>{children}</span>
          {isActive ? (
            <span
              aria-hidden
              className="absolute left-0 right-0 -bottom-1 h-[2px] rounded-full"
              style={{
                background:
                  "linear-gradient(90deg, rgba(212,175,55,0) 0%, #D4AF37 50%, rgba(212,175,55,0) 100%)",
                boxShadow: "0 0 8px rgba(212,175,55,0.65)",
              }}
            />
          ) : null}
        </>
      )}
    </NavLink>
  );
}

export function AppShell({ children }) {
  const { t } = useTranslation();
  const { isAuthed, user, logout } = useAuth();
  useLocation(); // subscribe so NavLinks re-evaluate isActive on nav

  return (
    <div className="relative min-h-screen text-white antialiased font-sans">
      {/* Background layers */}
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[#05060a]" />
      <div className="pointer-events-none fixed inset-0 -z-10 opacity-70 bg-[radial-gradient(900px_500px_at_85%_-10%,rgba(30,64,175,0.16),transparent),radial-gradient(700px_400px_at_-10%_110%,rgba(212,175,55,0.10),transparent)]" />
      <div className="pointer-events-none fixed inset-0 -z-10 opacity-[0.06] mix-blend-screen [background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%222%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')]" />

      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-[#05060a]/75 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-3 sm:px-8">
          <div className="flex items-center gap-6">
            <Logo />
            {isAuthed ? (
              <nav className="hidden md:flex items-center gap-5" data-testid="top-nav-primary">
                <TopNavLink to="/dashboard" testId="nav-dashboard">
                  {t("nav.dashboard")}
                </TopNavLink>
                <TopNavLink to="/auditor" testId="nav-auditor">
                  {t("nav.auditor")}
                </TopNavLink>
                <TopNavLink to="/history" testId="nav-history">
                  {t("nav.history")}
                </TopNavLink>
                <TopNavLink to="/billing" testId="nav-billing">
                  {t("nav.billing")}
                </TopNavLink>
              </nav>
            ) : null}
          </div>

          <nav className="flex items-center gap-2 sm:gap-3" data-testid="top-nav">
            {isAuthed ? (
              <>
                {user?.role === "admin" ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold uppercase tracking-wider text-[10px] border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F4D77A]"
                    data-testid="nav-admin-badge"
                    title="Admin account"
                  >
                    <ShieldCheck className="h-3 w-3" />
                    {t("wallet.admin_badge")}
                  </span>
                ) : null}
                <span
                  className="hidden sm:inline text-xs text-white/50"
                  data-testid="nav-user-email"
                >
                  {user?.email}
                </span>
                <button
                  onClick={logout}
                  className="text-sm text-white/75 hover:text-white transition-colors px-2"
                  data-testid="nav-logout-btn"
                >
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm text-white/75 hover:text-white transition-colors px-2"
                  data-testid="nav-login-link"
                >
                  {t("nav.login")}
                </Link>
                <Link
                  to="/register"
                  className="text-sm text-white/75 hover:text-white transition-colors px-2"
                  data-testid="nav-register-link"
                >
                  {t("nav.register")}
                </Link>
              </>
            )}
            <LanguageSwitcher className="ml-1" />
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8 sm:py-14">{children}</main>

      {/* Phase 8: floating Nexa-Core companion — only for authed users. */}
      {isAuthed ? <NexaCore /> : null}
    </div>
  );
}

export default AppShell;
